/**
 * Event Incident Module - 緊急事件與風險事件管理
 */

import { supabase } from '../core/supabaseClient'
import {
  EVENT_TYPES,
  EVENT_PRIORITIES,
  EVENT_LEVELS,
  EVENT_SOURCES,
  INCIDENT_LEVELS,
  INCIDENT_STATUSES,
  RISK_TYPES,
  RISK_LEVELS
} from './constants'
import {
  getIncidentStatusLabel,
  getRiskTypeLabel
} from './helpers'
import { createStandardEvent } from './eventFormat'
import type {
  EventRow,
  EventPriority,
  EventSource,
  RiskType,
  RiskLevel,
  EventFilters,
  ResponseLog,
  EventServiceContext
} from './types'

// ============ 緊急事件 (Incident) ============

/** 建立緊急事件 */
export function createIncidentEvent(
  ctx: EventServiceContext,
  data: {
    title: string
    description?: string
    level?: string
    status?: string
    organization_id?: string
    organization_name?: string
    field_id?: string
    field_name?: string
    device_id?: string
    device_name?: string
    equipment_name?: string
    assignee?: string
    measures?: unknown[]
  }
): EventRow {
  const priority: EventPriority =
    data.level === INCIDENT_LEVELS.CRITICAL ? EVENT_PRIORITIES.HIGH :
    data.level === INCIDENT_LEVELS.HIGH ? EVENT_PRIORITIES.HIGH :
    EVENT_PRIORITIES.MEDIUM

  return createStandardEvent(ctx, {
    type: EVENT_TYPES.INCIDENT,
    priority,
    title: data.title,
    content: data.description || '',
    organization_id: data.organization_id,
    organization_name: data.organization_name,
    field_id: data.field_id,
    field_name: data.field_name,
    device_id: data.device_id || '',
    device_name: data.device_name || data.equipment_name || '',
    event_level: EVENT_LEVELS.FIELD,
    source: EVENT_SOURCES.USER,
    extra_data: {
      incident_level: data.level || INCIDENT_LEVELS.MEDIUM,
      incident_status: data.status || INCIDENT_STATUSES.ACTIVE,
      assignee: data.assignee || null,
      first_response_at: null,
      resolved_at: null,
      measures: data.measures || [],
      response_logs: []
    }
  })
}

/** 取得緊急事件列表 */
export function getIncidentEvents(ctx: EventServiceContext, options: EventFilters = {}): EventRow[] {
  return ctx.getEventsByFilter({
    ...options,
    types: [EVENT_TYPES.INCIDENT]
  })
}

/** 依等級篩選緊急事件 */
export function getIncidentsByLevel(ctx: EventServiceContext, level: string): EventRow[] {
  return getIncidentEvents(ctx)
    .filter(e => (e.extra_data as Record<string, unknown>)?.incident_level === level)
}

/** 依狀態篩選緊急事件 */
export function getIncidentsByStatus(ctx: EventServiceContext, status: string): EventRow[] {
  return getIncidentEvents(ctx)
    .filter(e => (e.extra_data as Record<string, unknown>)?.incident_status === status)
}

/** 更新緊急事件狀態 */
export async function updateIncidentStatus(
  ctx: EventServiceContext,
  eventId: string,
  status: string,
  note = ''
): Promise<EventRow | null> {
  try {
    const event = ctx.eventCache.get(eventId)
    if (!event) {
      throw new Error(`找不到事件: ${eventId}`)
    }

    const currentExtraData = (event.extra_data || {}) as Record<string, unknown>
    const extra_data: Record<string, unknown> = {
      ...currentExtraData,
      incident_status: status
    }

    if (status === INCIDENT_STATUSES.RESOLVED) {
      extra_data.resolved_at = new Date().toISOString()
    }

    if (note) {
      const responseLogs = (currentExtraData.response_logs || []) as ResponseLog[]
      extra_data.response_logs = [
        ...responseLogs,
        {
          time: new Date().toISOString(),
          type: 'status_change',
          content: `狀態變更為「${getIncidentStatusLabel(status as Parameters<typeof getIncidentStatusLabel>[0])}」: ${note}`,
          user: ctx.currentUserId
        }
      ]
    }

    const isResolved = status === INCIDENT_STATUSES.RESOLVED
    const now = new Date().toISOString()

    const { error } = await supabase
      .from('events')
      .update({
        extra_data,
        is_resolved: isResolved,
        resolved_at: isResolved ? now : null,
        updated_at: now
      })
      .eq('id', eventId)

    if (error) throw error

    // 更新快取中的事件（注意：此處直接修改快取物件以維持與原有行為一致）
    event.extra_data = extra_data
    event.is_resolved = isResolved
    event.updated_at = now

    ctx.notifyListeners('event:updated', event)

    return event
  } catch (error) {
    console.error('[EventService] 更新緊急事件狀態失敗:', error)
    throw error
  }
}

/** 新增緊急事件響應記錄 */
export async function addIncidentResponseLog(
  ctx: EventServiceContext,
  eventId: string,
  log: { type?: string; content: string; user?: string }
): Promise<EventRow | null> {
  try {
    const event = ctx.eventCache.get(eventId)
    if (!event) {
      throw new Error(`找不到事件: ${eventId}`)
    }

    const currentExtraData = (event.extra_data || {}) as Record<string, unknown>
    const responseLogs = (currentExtraData.response_logs || []) as ResponseLog[]

    const newLogs: ResponseLog[] = [
      ...responseLogs,
      {
        time: new Date().toISOString(),
        type: log.type || 'action',
        content: log.content,
        user: log.user || ctx.currentUserId
      }
    ]

    const extra_data: Record<string, unknown> = {
      ...currentExtraData,
      response_logs: newLogs,
      first_response_at: currentExtraData.first_response_at || new Date().toISOString()
    }

    const now = new Date().toISOString()

    const { error } = await supabase
      .from('events')
      .update({
        extra_data,
        updated_at: now
      })
      .eq('id', eventId)

    if (error) throw error

    // 更新快取中的事件
    event.extra_data = extra_data
    event.updated_at = now

    ctx.notifyListeners('event:updated', event)

    return event
  } catch (error) {
    console.error('[EventService] 新增響應記錄失敗:', error)
    throw error
  }
}

// ============ 風險事件 (Risk) ============

/** 建立風險事件 */
export function createRiskEvent(
  ctx: EventServiceContext,
  data: {
    riskType: RiskType
    level?: RiskLevel
    description?: string
    fieldName?: string
    field_name?: string
    systemName?: string
    system_name?: string
    organization_id?: string
    organization_name?: string
    field_id?: string
    system_id?: string
    source?: EventSource
    value?: number
    suggestion?: string
    score?: number
  }
): EventRow {
  const fieldName = data.fieldName || data.field_name || '未知案場'
  const systemName = data.systemName || data.system_name || ''
  const displayName = systemName ? `${fieldName} - ${systemName}` : fieldName
  const title = `${getRiskTypeLabel(data.riskType)} - ${displayName}`

  const hasSystemId = data.system_id != null && data.system_id !== ''
  const eventLevel = hasSystemId ? EVENT_LEVELS.SYSTEM : EVENT_LEVELS.FIELD

  return createStandardEvent(ctx, {
    type: EVENT_TYPES.RISK,
    priority: data.level || EVENT_PRIORITIES.MEDIUM,
    title,
    content: data.description || '',
    organization_id: data.organization_id,
    organization_name: data.organization_name,
    field_id: data.field_id,
    field_name: fieldName,
    system_id: data.system_id || '',
    system_name: systemName,
    event_level: eventLevel,
    source: data.source || EVENT_SOURCES.SYSTEM,
    extra_data: {
      risk_type: data.riskType,
      risk_level: data.level || RISK_LEVELS.MEDIUM,
      risk_value: data.value || null,
      suggestion: data.suggestion || '',
      risk_score: data.score || null,
      system_name: systemName
    }
  })
}

/** 取得風險事件列表 */
export function getRiskEvents(ctx: EventServiceContext, options: EventFilters = {}): EventRow[] {
  return ctx.getEventsByFilter(options)
    .filter(e => e.source_detail === 'risk_analysis' || e.source_detail === 'system_risk_analysis')
}

/** 依類型篩選風險事件 */
export function getRiskEventsByType(ctx: EventServiceContext, riskType: RiskType): EventRow[] {
  return getRiskEvents(ctx)
    .filter(e => (e.extra_data as Record<string, unknown>)?.risk_type === riskType)
}

/** 依等級篩選風險事件 */
export function getRiskEventsByLevel(ctx: EventServiceContext, level: RiskLevel): EventRow[] {
  return getRiskEvents(ctx)
    .filter(e => (e.extra_data as Record<string, unknown>)?.risk_level === level)
}

// ============ 統計 ============

/** 風險事件統計 */
export function getRiskStats(ctx: EventServiceContext): {
  total: number
  byType: Record<string, number>
  byLevel: Record<string, number>
} {
  const riskEvents = getRiskEvents(ctx)

  return {
    total: riskEvents.length,
    byType: {
      overload: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_type === RISK_TYPES.OVERLOAD).length,
      generation: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_type === RISK_TYPES.GENERATION).length,
      weather: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_type === RISK_TYPES.WEATHER).length,
      equipment: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_type === RISK_TYPES.EQUIPMENT).length
    },
    byLevel: {
      high: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_level === RISK_LEVELS.HIGH).length,
      medium: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_level === RISK_LEVELS.MEDIUM).length,
      low: riskEvents.filter(e => (e.extra_data as Record<string, unknown>)?.risk_level === RISK_LEVELS.LOW).length
    }
  }
}

/** 緊急事件統計 */
export function getIncidentStats(ctx: EventServiceContext): {
  total: number
  byLevel: Record<string, number>
  byStatus: Record<string, number>
} {
  const incidentEvents = getIncidentEvents(ctx)

  return {
    total: incidentEvents.length,
    byLevel: {
      critical: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_level === INCIDENT_LEVELS.CRITICAL).length,
      high: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_level === INCIDENT_LEVELS.HIGH).length,
      medium: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_level === INCIDENT_LEVELS.MEDIUM).length
    },
    byStatus: {
      active: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_status === INCIDENT_STATUSES.ACTIVE).length,
      pending: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_status === INCIDENT_STATUSES.PENDING).length,
      processing: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_status === INCIDENT_STATUSES.PROCESSING).length,
      resolved: incidentEvents.filter(e => (e.extra_data as Record<string, unknown>)?.incident_status === INCIDENT_STATUSES.RESOLVED).length
    }
  }
}
